import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, Loader2, Paperclip, Wallet } from "lucide-react";
import { useClaim, useSubmitClaim, useCancelClaim, useMarkClaimPaid } from "../hooks";
import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ClaimDocumentViewer } from "../components/ClaimDocumentViewer";
import type { ClaimDocument } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── per-line documents ───────────────────────────────────────────────────────

function LineDocList({
  claimId,
  lineId,
  docs,
  isFirst,
}: {
  claimId: number;
  lineId: number;
  docs: ClaimDocument[];
  isFirst: boolean;
}) {
  const backendSupportsLineId = docs.some((d) => d.lineId != null);
  const lineDocs = backendSupportsLineId
    ? docs.filter((d) => d.lineId === lineId)
    : isFirst ? docs : [];
  if (lineDocs.length === 0) {
    return isFirst || backendSupportsLineId
      ? <p className="mt-1.5 text-[10px] italic text-slate-400">No document attached</p>
      : null;
  }
  return (
    <div className="mt-2 space-y-1.5">
      {lineDocs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-1.5">
          <Paperclip className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
            {d.originalFileName}
          </span>
          <ClaimDocumentViewer claimId={claimId} doc={d} compact />
        </div>
      ))}
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function ClaimDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const claimId = Number(id);

  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const canMarkPaid = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"])
    || hasPermission("HR_CLAIM_APPROVE") || hasPermission("FINANCE_CLAIM_APPROVE");

  const { data, isLoading } = useClaim(claimId);

  const submit  = useSubmitClaim();
  const cancel  = useCancelClaim();
  const markPaid = useMarkClaimPaid();
  const confirm = useConfirm();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading claim…
      </div>
    );
  }
  if (!data) {
    return <p className="p-6 text-sm text-red-600">Claim not found.</p>;
  }

  const isDraft    = data.status === "DRAFT";
  const backendSupportsLineId = data.documents.some((d) => d.lineId != null);
  const linesWithoutDoc = backendSupportsLineId
    ? data.lines.filter((l) => !data.documents.some((d) => d.lineId === l.id))
    : [];
  const canSubmit  = isDraft && data.lines.length > 0 &&
    (backendSupportsLineId ? linesWithoutDoc.length === 0 : data.documents.length > 0);

  // Claim-level docs only shown when backend supports lineId (otherwise shown inline on first line)
  const claimLevelDocs = backendSupportsLineId
    ? data.documents.filter((d) => !d.lineId)
    : [];

  return (
    <div className="flex flex-col gap-5">

      {/* ── Back ── */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 self-start text-sm font-semibold text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* ── Header card ── */}
      <div className="flex flex-col gap-4 rounded-2xl border bg-white p-5 shadow-sm sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-black text-slate-900">{data.claimNo}</h1>
            <ClaimStatusBadge status={data.status} />
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {typeLabel(data.claimType)}
            {data.purpose && <span className="mx-1.5 text-slate-300">·</span>}
            {data.purpose && <span className="italic">{data.purpose}</span>}
          </p>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
            <span><strong className="text-slate-700">Employee:</strong> {data.employeeName}</span>
            {data.submittedAt && <span><strong className="text-slate-700">Submitted:</strong> {fmtDate(data.submittedAt)}</span>}
            {data.approvedAt  && <span><strong className="text-slate-700">Approved:</strong> {fmtDate(data.approvedAt)}</span>}
            {data.paidAt      && <span><strong className="text-slate-700">Paid:</strong> {fmtDate(data.paidAt)}</span>}
            {data.currentStepName && <span><strong className="text-slate-700">Step:</strong> {data.currentStepName}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-slate-900">{fmt(data.totalAmount)}</div>
          <div className="text-xs text-slate-400">Total amount</div>
        </div>
      </div>

      {/* ── Lines + per-line docs ── */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="border-b bg-slate-50 px-5 py-3">
          <h2 className="font-black text-slate-900">Claim Lines</h2>
          <p className="mt-0.5 text-xs text-slate-400">Each line includes its supporting document.</p>
        </div>

        {data.lines.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">No lines added.</p>
        ) : (
          <div className="divide-y">
            {data.lines.map((line, idx) => (
              <div key={line.id} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                    {idx + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-bold text-slate-800">{line.description}</p>
                        <p className="text-xs text-slate-400">{fmtDate(line.expenseDate)}</p>
                      </div>
                      <span className="shrink-0 font-black text-emerald-700">
                        {fmt(line.amount)}
                      </span>
                    </div>
                    <LineDocList
                      claimId={data.id}
                      lineId={line.id}
                      docs={data.documents}
                      isFirst={idx === 0}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total row */}
        <div className="flex justify-end border-t bg-slate-50 px-5 py-3">
          <span className="text-sm font-black text-slate-700">
            Total: {fmt(data.lines.reduce((s, l) => s + l.amount, 0))}
          </span>
        </div>
      </div>

      {/* ── Claim-level docs (legacy / extra) ── */}
      {claimLevelDocs.length > 0 && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b bg-slate-50 px-5 py-3">
            <h2 className="font-black text-slate-900">Additional Documents</h2>
          </div>
          <div className="divide-y">
            {claimLevelDocs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-5 py-3">
                <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">
                  {d.originalFileName}
                </span>
                <ClaimDocumentViewer claimId={data.id} doc={d} compact />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      {isDraft && (
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm sm:flex-row sm:justify-end">
          <Link
            to={`/claims/${data.id}/edit`}
            className="inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            Edit claim
          </Link>
          <button
            type="button"
            disabled={cancel.isPending}
            onClick={async () => {
              if (!(await confirm("Cancel this claim?"))) return;
              await cancel.mutateAsync({ id: data.id, comments: "Cancelled by employee" });
              navigate("/claims/mine");
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            Cancel claim
          </button>
          <button
            type="button"
            disabled={!canSubmit || submit.isPending}
            onClick={async () => {
              await submit.mutateAsync(data.id);
              // query invalidation in useSubmitClaim refetches and updates
              // the page in place — no navigation needed
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            {submit.isPending ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      )}

      {/* ── Mark as Paid (finance / admin only, APPROVED status) ── */}
      {canMarkPaid && data.status === "APPROVED" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-indigo-800">Ready for payment</p>
            <p className="mt-0.5 text-xs text-indigo-600">
              This claim has been approved. Mark it as paid once the payment has been processed.
            </p>
          </div>
          <button
            type="button"
            disabled={markPaid.isPending}
            onClick={async () => {
              if (!(await confirm(`Mark ${data.claimNo} as paid (${fmt(data.totalAmount)})?`))) return;
              try {
                await markPaid.mutateAsync(data.id);
              } catch {
                // error shown below
              }
            }}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-indigo-700 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-800 disabled:opacity-50"
          >
            <Wallet className="h-4 w-4" />
            {markPaid.isPending ? "Processing…" : "Mark as Paid"}
          </button>
          {markPaid.isError && (
            <p className="text-xs font-semibold text-red-600">Failed to mark as paid. Please try again.</p>
          )}
        </div>
      )}

      {isDraft && data.lines.length > 0 && linesWithoutDoc.length > 0 && (
        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {linesWithoutDoc.length} line{linesWithoutDoc.length !== 1 ? "s" : ""} still need a supporting document.
          <Link to={`/claims/${data.id}/edit`} className="ml-2 font-bold underline">
            Edit claim
          </Link>
        </p>
      )}

    </div>
  );
}
